import { PLAN_ALL_GENERATION_RETRY_TIMES } from "@/constants/plan";
import { QueryKeys } from "@/constants/query-keys";
import { useGetAllGenerationStatus } from "@/hooks/requests/generation-status/use-get-all-generation-status";
import { useCreatePlan } from "@/hooks/requests/plan/use-create-plan";
import { useToast } from "@/hooks/ui/use-toast";
import { recreatePlanInvalidateQueries } from "@/utils/query/invalidate";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

export const useCreateMealPlan = (onSuccess?: () => void) => {
  const counterRef = useRef(0);
  const queryClient = useQueryClient();
  const timerRef = useRef<NodeJS.Timeout>();
  const { allGenerationStatus, refetch } = useGetAllGenerationStatus();
  const { updatePlan, isError, error } = useCreatePlan();
  const { showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    startUpdateTimer();
    return () => {
      stopUpdateTimer();
    };
  }, []);

  useEffect(() => {
    console.info("allGenerationStatus", allGenerationStatus);
    if (allGenerationStatus?.allComplete) {
      stopUpdateTimer();
      createPlan();
    }
    if (allGenerationStatus?.anyFailed) {
      const failedMealTimes = Object.keys(
        allGenerationStatus?.statuses ?? {},
      ).filter(
        (mealTime) => allGenerationStatus?.statuses?.[mealTime] === "failed",
      );
      if (failedMealTimes.length > 0) {
        stopUpdateTimer();
        showError(
          `Failed to create meal plan for ${failedMealTimes.join(", ")}`,
        );
      }
    }
  }, [allGenerationStatus]);

  const startUpdateTimer = useCallback(() => {
    setIsLoading(true);
    timerRef.current = setInterval(() => {
      if (counterRef.current > PLAN_ALL_GENERATION_RETRY_TIMES) {
        stopUpdateTimer();
        createPlan();
        return;
      }
      counterRef.current++;
      refetch();
    }, 2000);
  }, [refetch]);

  const stopUpdateTimer = useCallback(() => {
    if (timerRef.current) {
      console.info("stopUpdateTimer");
      clearInterval(timerRef.current);
    }
  }, []);

  const createPlan = useCallback(() => {
    setIsLoading(true);
    updatePlan(undefined, {
      onSuccess: () => {
        onSuccess?.();
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.ALL_GENERATION_STATUS],
        });

        recreatePlanInvalidateQueries(queryClient);
        setIsLoading(false);
      },
      onError: (error) => {
        showError(error.message);
        setIsLoading(false);
      },
    });
  }, [updatePlan, onSuccess]);

  return {
    startUpdateTimer,
    stopUpdateTimer,
    isLoading,
    isCompleted: allGenerationStatus?.allComplete ?? false,
    isError,
    error,
    allGenerationStatus,
  };
};
